/// <reference types="google.maps" />
import React from 'react'
import { Autocomplete, Box, Typography, debounce } from '@mui/material'
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps'
import { CheckoutTextField } from './fields'
import type { ResolvedAddress } from './schema'

type Suggestion = { placeId: string; description: string }

type Props = {
  id?: string
  label: string
  value: string
  onChange: (formattedAddress: string) => void
  onResolve?: (address: ResolvedAddress | null) => void
  onValidate?: (error: string | null) => void
  onBlur?: () => void
  error?: boolean
  helperText?: string
  placeholder?: string
}

export const NO_BUSINESS_ERROR = 'No business is registered at this address'

const INCOMPLETE_ADDRESS_ERROR =
  'Select a complete address (street, city, state, ZIP)'

const isBusinessPlace = (place: google.maps.places.Place): boolean => {
  if (place.businessStatus) return true
  const types = place.types ?? []
  return types.includes('establishment') || types.includes('point_of_interest')
}

/** Confirm Google lists a business at the picked coordinates (address picks
 * aren't themselves tagged as businesses, so search the immediate vicinity). */
const hasNearbyBusiness = async (
  places: google.maps.PlacesLibrary,
  location: google.maps.LatLng,
): Promise<boolean> => {
  try {
    const { places: found } = await places.Place.searchNearby({
      fields: ['id', 'types', 'businessStatus'],
      locationRestriction: { center: location, radius: 50 },
      maxResultCount: 5,
    })
    return found.some(isBusinessPlace)
  } catch (e) {
    console.error('Nearby business lookup failed:', e)
    return false
  }
}

const stripCountry = (text?: string): string =>
  (text ?? '').replace(/,?\s*USA$/, '')

const formatDisplay = (a: ResolvedAddress): string =>
  `${a.street_address}, ${a.city}, ${a.state_code} ${a.postal_code}`

type ComponentField = {
  type: string
  short?: boolean
  fallbacks?: string[]
}

const pick = (
  components: google.maps.places.AddressComponent[],
  field: ComponentField,
): string => {
  const types = [field.type, ...(field.fallbacks ?? [])]
  for (const t of types) {
    const c = components.find((comp) => comp.types.includes(t))
    if (!c) continue
    const v = field.short
      ? (c.shortText ?? c.longText)
      : (c.longText ?? c.shortText)
    if (v) return v
  }
  return ''
}

const resolveAddress = (
  place: google.maps.places.Place,
): ResolvedAddress | null => {
  const components = place.addressComponents
  const loc = place.location
  const placesId = place.id
  if (!components || !loc || !placesId) return null
  const streetNumber = pick(components, { type: 'street_number' })
  const route = pick(components, { type: 'route' })
  const street = [streetNumber, route].filter(Boolean).join(' ')
  const city = pick(components, {
    type: 'locality',
    fallbacks: ['sublocality', 'postal_town'],
  })
  const state = pick(components, {
    type: 'administrative_area_level_1',
    short: true,
  })
  const postal = pick(components, { type: 'postal_code' })
  const country = pick(components, { type: 'country', short: true })
  if (!street || !city || !state || !postal || !country) return null
  return {
    street_address: street,
    city,
    state_code: state,
    postal_code: postal,
    country,
    coordinates: { lat: loc.lat(), lng: loc.lng() },
    places_id: placesId,
  }
}

const Inner: React.FC<Props> = ({
  id,
  label,
  value,
  onChange,
  onResolve,
  onValidate,
  onBlur,
  error,
  helperText,
  placeholder,
}) => {
  const places = useMapsLibrary('places')
  const [inputValue, setInputValue] = React.useState(value)
  const [options, setOptions] = React.useState<Suggestion[]>([])
  const [open, setOpen] = React.useState(false)
  const sessionTokenRef =
    React.useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  React.useEffect(() => {
    if (!places?.AutocompleteSessionToken) return
    sessionTokenRef.current = new places.AutocompleteSessionToken()
  }, [places])

  const fetchSuggestions = React.useMemo(
    () =>
      debounce(async (input: string) => {
        if (!places?.AutocompleteSuggestion || !input) {
          setOptions([])
          return
        }
        try {
          const { suggestions } =
            await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input,
              sessionToken: sessionTokenRef.current ?? undefined,
              includedRegionCodes: ['us'],
            })
          setOptions(
            suggestions
              .map((s) => {
                const p = s.placePrediction
                if (!p) return null
                const main = p.mainText?.toString() ?? p.text.toString()
                const secondary = stripCountry(p.secondaryText?.toString())
                const description = secondary ? `${main}, ${secondary}` : main
                return { placeId: p.placeId, description }
              })
              .filter((s): s is Suggestion => s !== null),
          )
        } catch (e) {
          console.error('Places autocomplete failed:', e)
          setOptions([])
        }
      }, 300),
    [places],
  )

  React.useEffect(() => {
    if (!inputValue) {
      setOptions([])
      return
    }
    void fetchSuggestions(inputValue)
  }, [inputValue, fetchSuggestions])

  const handleSelect = async (suggestion: Suggestion) => {
    if (!places?.Place) {
      onChange(suggestion.description)
      setInputValue(suggestion.description)
      onResolve?.(null)
      return
    }
    try {
      const place = new places.Place({ id: suggestion.placeId })
      await place.fetchFields({
        fields: [
          'formattedAddress',
          'addressComponents',
          'location',
          'id',
          'types',
          'businessStatus',
        ],
      })
      const resolved = resolveAddress(place)
      const formatted = resolved
        ? formatDisplay(resolved)
        : stripCountry(place.formattedAddress ?? suggestion.description)
      onChange(formatted)
      setInputValue(formatted)
      if (!resolved) {
        onResolve?.(null)
        onValidate?.(INCOMPLETE_ADDRESS_ERROR)
      } else if (
        isBusinessPlace(place) ||
        (place.location && (await hasNearbyBusiness(places, place.location)))
      ) {
        onResolve?.(resolved)
        onValidate?.(null)
      } else {
        onResolve?.(null)
        onValidate?.(NO_BUSINESS_ERROR)
      }
      sessionTokenRef.current = new places.AutocompleteSessionToken()
    } catch (e) {
      console.error('Place details fetch failed:', e)
      onChange(suggestion.description)
      setInputValue(suggestion.description)
      onResolve?.(null)
    }
  }

  return (
    <Autocomplete
      open={open && options.length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      getOptionLabel={(o) => o.description}
      isOptionEqualToValue={(o, v) => o.placeId === v.placeId}
      filterOptions={(x) => x}
      inputValue={inputValue}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') {
          setInputValue(v)
          onChange(v)
          onResolve?.(null)
          onValidate?.(null)
        }
        if (reason === 'clear') {
          onChange('')
          setInputValue('')
          onResolve?.(null)
          onValidate?.(null)
        }
      }}
      onChange={(_, v) => {
        if (v) void handleSelect(v)
      }}
      onBlur={onBlur}
      renderInput={(params) => (
        <CheckoutTextField
          {...params}
          id={id}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          autoComplete="street-address"
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.placeId}>
          <Typography variant="body4">{option.description}</Typography>
        </Box>
      )}
    />
  )
}

export const AddressAutocomplete: React.FC<Props> = (props) => (
  <APIProvider apiKey={process.env.GATSBY_GOOGLE_API_KEY ?? ''}>
    <Inner {...props} />
  </APIProvider>
)
